/*
 * hh-atc.js - keeps Add to cart on the page.
 *
 * Every add-to-cart on the site was a native form POST to /cart/add, which made the
 * browser navigate away to /cart. On a product page that throws away the thing the
 * visitor was reading; on the homepage quick-add it throws away the whole grid. It is
 * the single largest avoidable drop-off in the funnel.
 *
 * This intercepts any form that posts to /cart/add, submits it with fetch, and slides
 * a confirmation panel in from the right with Checkout / Keep shopping. The page the
 * visitor was on stays exactly where it was.
 *
 * It also fires AddToCart on the Meta and TikTok pixels, and ViewContent on product
 * pages. The old full-page redirect made AddToCart unreliable, which starves ad
 * optimisation of its most important signal.
 *
 * Progressive enhancement: if fetch fails for any reason, the form is submitted
 * natively and the visitor gets the old behaviour rather than nothing.
 */
(function () {
  var MONEY_FORMAT = (window.Shopify && window.Shopify.money_format) || '${{amount}}';

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = parts.join('.');
    if (/\{\{\s*amount\s*\}\}/.test(MONEY_FORMAT)) {
      return MONEY_FORMAT.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return '$' + amount;
  }

  function track(event, payload) {
    try { if (window.fbq) window.fbq('track', event, payload); } catch (e) {}
    try { if (window.ttq && window.ttq.track) window.ttq.track(event, payload); } catch (e) {}
  }

  // ---- drawer ---------------------------------------------------------------

  var drawer, lastFocus;

  function els() {
    if (!drawer) drawer = document.getElementById('hh-atc-drawer');
    return drawer;
  }

  function openDrawer() {
    var d = els();
    if (!d) return;
    lastFocus = document.activeElement;
    d.hidden = false;
    // Next frame so the transition actually runs.
    requestAnimationFrame(function () { d.classList.add('is-open'); });
    document.documentElement.style.overflow = 'hidden';
    var close = d.querySelector('[data-hh-atc-close]');
    if (close) close.focus();
  }

  function closeDrawer() {
    var d = els();
    if (!d) return;
    d.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    window.setTimeout(function () { d.hidden = true; }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function renderDrawer(item, cart) {
    var d = els();
    if (!d) return;

    var img = d.querySelector('[data-hh-atc-image]');
    if (img) {
      if (item.image) {
        img.src = item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2');
        img.alt = item.product_title || '';
        img.hidden = false;
      } else {
        img.hidden = true;
      }
    }

    var title = d.querySelector('[data-hh-atc-title]');
    if (title) title.textContent = (item.product_title || '').split(' | ')[0].split(' – ')[0];

    var variant = d.querySelector('[data-hh-atc-variant]');
    if (variant) {
      var vt = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      variant.textContent = vt;
      variant.hidden = !vt;
    }

    var price = d.querySelector('[data-hh-atc-price]');
    if (price) price.textContent = formatMoney(item.final_line_price != null ? item.final_line_price : item.final_price);

    var count = d.querySelector('[data-hh-atc-count]');
    if (count) count.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');

    var subtotal = d.querySelector('[data-hh-atc-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
  }

  function syncHeaderCount(cart) {
    var nodes = document.querySelectorAll('.cart-bubble, [data-cart-count], .cart-count');
    for (var i = 0; i < nodes.length; i++) {
      if (/^\s*\d+\s*$/.test(nodes[i].textContent)) nodes[i].textContent = String(cart.item_count);
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
  }

  // ---- add to cart ----------------------------------------------------------

  function isAddForm(form) {
    var action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') !== -1;
  }

  function submitting(form, on) {
    var btn = form.querySelector('[type="submit"]');
    if (!btn) return;
    if (on) {
      btn.dataset.hhLabel = btn.textContent;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Adding…';
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      if (btn.dataset.hhLabel) btn.textContent = btn.dataset.hhLabel;
    }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !isAddForm(form)) return;
    if (!window.fetch || !els()) return; // no drawer or no fetch: let the browser do it

    e.preventDefault();
    submitting(form, true);

    var body = new FormData(form);
    // The Section Rendering API is not needed here; cart.js gives us everything.
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      })
      .then(function (item) {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            syncHeaderCount(cart);
            renderDrawer(item, cart);
            openDrawer();
            track('AddToCart', {
              content_ids: [String(item.product_id)],
              content_type: 'product',
              content_name: item.product_title,
              value: (item.final_line_price || item.final_price) / 100,
              currency: cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD'
            });
          });
      })
      .catch(function (err) {
        // Sold out, or a variant that no longer exists: surface it, do not swallow it.
        var msg = err && err.description ? err.description : null;
        if (msg) {
          window.alert(msg);
        } else {
          form.submit(); // fall back to the native POST
        }
      })
      .then(function () { submitting(form, false); });
  });

  // ---- drawer wiring --------------------------------------------------------

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-hh-atc-close]') || e.target.closest('[data-hh-atc-backdrop]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var d = els();
      if (d && d.classList.contains('is-open')) closeDrawer();
    }
  });

  // ---- ViewContent ----------------------------------------------------------

  function fireViewContent() {
    var node = document.querySelector('[data-hh-product-json]');
    if (!node) return;
    var p;
    try { p = JSON.parse(node.textContent); } catch (e) { return; }
    if (!p || !p.id) return;
    track('ViewContent', {
      content_ids: [String(p.id)],
      content_type: 'product',
      content_name: p.title,
      value: p.price / 100,
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD'
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fireViewContent);
  else fireViewContent();
})();
